import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { LuxToast } from '@lux/wardrobe-lib'
import AppLoader from './../components/AppLoader/AppLoader'
import FabButton from './../components/FabButton/FabButton'
import BackToTop from './../components/BackToTop/BackToTop'
import { OverlayAction } from './../redux/reducers/overlay'
import {
	getBrowsingTool,
	getBrowsingToolNotifications,
} from './../redux/actions/notifications'
import {
	openProjectCreation,
	closeProjectCreation,
	setShowAppLoader,
	setIsAppLoaded,
	openResourceCreation,
	openDocumentCreation,
	openSubfolderCreation,
	openFolderCreation,
	openDiscussionCreation,
	setShowAddPhotoDialog,
	openPhotoCreation,
	showToastUploadPhotoError,
	openChatbot,
} from './../redux/actions/overlay'
import {
	successActions,
	errorsActions,
	successSelectors,
	errorsSelectors,
	UserHelper,
	EventsType,
	loadingSelectors,
	layoutActions,
	ModalsType,
} from '@Techedge/digitalportal-shared'
import DialogUploadPhoto from '../components/DialogUploadPhoto/DialogUploadPhoto'
import { GalleryService } from '../services/services'

const acceptedImageFormat =
	'.apng,.avif,.gif,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.png,.svg,.webp,.bmp,.ico,.cur,.tif,.tiff.APNG,.AVIF,.GIF,.JPG,.JPEG,.JFIF,.PJPEG,.PJP,.PNG,.SVG,.WEBP,.BMP,.ICO,.CUR,.TIF,.TIFF'

export const Overlay = (props: any) => {
	const {
		loading,
		success,
		error,
		clearToast,
		showProjectCreation,
		showFabButton,
		showBackToTop,
		showAppLoader,
		isAppLoaded,
		getBrowsingTool,
		showAddPhotoDialog,
		setShowAddPhotoDialog,
		showToastUploadPhotoError,
		openSavePictureModal,
	} = props

	const intl = useIntl()

	const toast = useRef(null)

	const [toastItems, setToastItems] = useState([])
	const [uploadLoading, setUploadLoading] = useState(false)

	const toastLife = process.env.SMARTLY_OVERLAY_TOAST_LIFE || 10000

	const pathNames = [
		'/home',
		'/calendar',
		'/activity',
		'/notifications',
		'/resources',
		'/gallery',
		'/menu/forum',
		'/menu/quick-links',
		'/menu/store-list',
		'/reporting',
		'/menu/grsd',
		'/menu/profile-mgmt',
		'/menu/profile',
	]

	useEffect(() => {
		// get browsing tool notifications
		if (!!UserHelper._shared().accessToken()) {
			// NotificationsApi.getBrowsingToolNotifications()
			getBrowsingTool()
		}
		// set showAppLoader true && isAppLoaded false when location changes and match pathNames
		window.addEventListener('popstate', () => {
			let pathName = window.location.pathname
			if (pathNames.includes(pathName)) {
				props.setShowAppLoader(true)
				props.setIsAppLoaded(false)
			}
		})
		// window toast handler
		const handler = (e) => {
			if (e.detail) {
				const newToast = {
					id: Math.random() * 100,
					severity: e.detail.severity,
					summary: e.detail.title,
					detail: e.detail.text,
				}
				const newItems = [...toastItems, newToast]
				setToastItems(newItems)
			}
		}
		window.addEventListener(EventsType.WINDOW_TOAST, handler)
		return () => {
			window.removeEventListener(EventsType.WINDOW_TOAST, handler)
		}
	}, [])

	useEffect(() => {
		props.setShowAppLoader(loading)
		if (!loading) {
			props.setIsAppLoaded(true)
		}
	}, [loading])

	useEffect(() => {
		if (!!success && success.length > 0) {
			setToastList(success)
		}
	}, [success])

	useEffect(() => {
		const newError = error.map((err) => ({
			...err,
			text: err.text.message || err.text,
		}))
		if (!!newError && newError.length > 0) {
			setToastList(newError, 'warn')
		}
	}, [error])

	useEffect(() => {
		if (showAppLoader && !isAppLoaded) {
			setTimeout(() => {
				props.setShowAppLoader(false)
			}, 4000)
		}
	}, [showAppLoader, isAppLoaded])

	const setToastList = (list, severity?) => {
		const newItems = list.map((toast) => ({
			id: toast.id,
			severity: severity || toast.isMostUrgent ? 'warn' : 'success',
			summary: toast.title,
			detail: toast.text,
			life: toastLife,
			sticky: severity === 'warn',
			ctaLabel: toast.action
				? intl.formatMessage({
						id: 'overlay.toast.action',
						defaultMessage: 'go to',
				  })
				: '',
			ctaHandler: toast.action
				? () => {
						window.location.replace(toast.action)
				  }
				: null,
		}))
		setToastItems(newItems)
		clearToast(newItems.map((item) => item.id))
	}

	const onMenuItemHandler = (action: OverlayAction) => {
		if (!!action) props[action]()
	}

	const onFormatError = () => {
		showToastUploadPhotoError(
			intl.formatMessage({
				id: 'modals.new.activity.section.main.info.project.cover.format.error',
				defaultMessage:
					'Selected image format cannot be processed, please use images with one of the following formats: .apng,.avif,.gif,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.png,.svg,.webp, .bmp,.ico,.cur,.tif,.tiff',
			})
		)
	}

	const onSizeError = () => {
		showToastUploadPhotoError(
			intl.formatMessage({
				id: 'modals.new.activity.section.main.info.project.cover.size.error',
				defaultMessage: 'Selected image exceeds the size limit of 20MB',
			})
		)
	}

	const uploadImageList = (files, uploadedImageList = []) => {
		setUploadLoading(true)
		const file = files[0]
		const newFiles = [...files]
		newFiles.shift()
		const reader = new FileReader()
		reader.onload = async (evt) => {
			try {
				await GalleryService.uploadFile(
					evt.target.result,
					file.name,
					file.type
				).then((res) => {
					uploadedImageList = [
						...uploadedImageList,
						{
							name: file.name,
							url: file.url,
							attachmentId: res.data.id,
						},
					]
					if (newFiles.length > 0) {
						uploadImageList(newFiles, uploadedImageList)
						return
					}
					setUploadLoading(false)
					setShowAddPhotoDialog(false)
					openSavePictureModal({
						imageList: uploadedImageList,
						isUpload: true,
					})
				})
			} catch (err) {}
		}
		reader.readAsDataURL(file.fileObj)
	}

	const onFileChange = function (e) {
		e.preventDefault()
		if (!Object.values(e.target.files).length) return
		let formatError = false
		let sizeError = false
		Object.values(e.target.files).forEach((f: File) => {
			formatError =
				formatError ||
				acceptedImageFormat.indexOf(f.name.split('.').pop()) === -1
			sizeError = sizeError || f.size > 20971520
		})
		if (formatError) {
			onFormatError()
			return
		}
		if (sizeError) {
			onSizeError()
			return
		}
		const newFiles = Object.values(e.target.files).map((f: any) => ({
			name: f.name,
			url: URL.createObjectURL(f),
			fileObj: f,
		}))
		uploadImageList(newFiles)
	}

	return (
		<div id="smart-overlay">
			<LuxToast
				elref={toast}
				toastItems={toastItems}
				className={
					showFabButton
						? 'smart-overlay-toast--with-fab-button'
						: 'smart-overlay-toast'
				}
			/>
			<AppLoader />
			{!showProjectCreation && showFabButton && (
				<FabButton
					onMenuItem={onMenuItemHandler}
					smartlyPermissionUpdated={props.smartlyPermissionUpdated}
					openChatbot={openChatbot}
				/>
			)}
			{showBackToTop && <BackToTop />}
			<DialogUploadPhoto
				visible={showAddPhotoDialog}
				multiple
				loading={uploadLoading}
				onFileChange={onFileChange}
				onHide={() => setShowAddPhotoDialog(false)}
			/>
		</div>
	)
}

const successAction = successSelectors.createSuccessToastSelector()
const errorAction = errorsSelectors.createErrorToastSelector()
const loadingAction = loadingSelectors.createLoadingSelector([
	'USER',
	'USER_PERMISSIONS',
	'GET_NEWS',
	'GET_NOTIFICATIONS',
	'GET_ACTIVITY_LIST'
])

const mapStateToProps = (state: any) => ({
	success: state.successReducer.toastList,
	error: errorAction(state),
	loading: loadingAction(state),
	showProjectCreation: state.overlayReducer?.showProjectCreation,
	showFabButton: state.layoutReducer.showFabButton,
	showBackToTop: state.layoutReducer.showBackToTop,
	showAppLoader: state.overlayReducer?.showAppLoader,
	showAddPhotoDialog: state.overlayReducer?.showAddPhotoDialog,
	isAppLoaded: state.overlayReducer?.isAppLoaded,
	smartlyPermissionUpdated: state.userReducer.smartlyPermissionUpdated,
})

const mapDispatchToProps = (dispatch: any) => ({
	getBrowsingToolNotifications: () =>
		dispatch(getBrowsingToolNotifications()),
	getBrowsingTool: () => dispatch(getBrowsingTool()),
	clearSuccess: () => dispatch(successActions.clearAllSuccess()),
	clearToast: (toastIds: number[]) =>
		dispatch(successActions.clearToast(toastIds)),
	clearError: () => dispatch(errorsActions.clearAllError()),
	openProjectCreation: () => dispatch(openProjectCreation()),
	closeProjectCreation: () => dispatch(closeProjectCreation()),
	openResourceCreation: () => dispatch(openResourceCreation()),
	openDocumentCreation: () => dispatch(openDocumentCreation()),
	openChatbot: () => dispatch(openChatbot()),
	openSubfolderCreation: () => dispatch(openSubfolderCreation()),
	openFolderCreation: () => dispatch(openFolderCreation()),
	openDiscussionCreation: () => dispatch(openDiscussionCreation()),
	openPhotoCreation: () => dispatch(openPhotoCreation()),
	setShowAppLoader: (value: boolean) => dispatch(setShowAppLoader(value)),
	setShowAddPhotoDialog: (value: boolean) =>
		dispatch(setShowAddPhotoDialog(value)),
	setIsAppLoaded: (value: boolean) => dispatch(setIsAppLoaded(value)),
	showToastUploadPhotoError: (text: string) => {
		dispatch(showToastUploadPhotoError(text))
	},
	openSavePictureModal: (props) => {
		dispatch(
			layoutActions.setModals({
				type: ModalsType.SAVE_PICTURE,
				props,
			})
		)
	},
})

export default connect(mapStateToProps, mapDispatchToProps)(Overlay)
